import styled, { css, keyframes } from 'styled-components';

export const DashboardContainer = styled.div`
  padding: 0;
  background: transparent;
  font-family: 'Plus Jakarta Sans', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/* ───────── Skeleton + transitions ───────── */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const Skeleton = styled.div<{
  $w?: string | number;
  $h?: string | number;
  $radius?: number;
  $mb?: number;
}>`
  display: block;
  width: ${({ $w }) => (typeof $w === 'number' ? `${$w}px` : $w || '100%')};
  height: ${({ $h }) => (typeof $h === 'number' ? `${$h}px` : $h || '14px')};
  border-radius: ${({ $radius }) => $radius ?? 6}px;
  margin-bottom: ${({ $mb }) => $mb ?? 0}px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

export const SkeletonCircle = styled.div<{ $size?: number }>`
  width: ${({ $size }) => $size ?? 120}px;
  height: ${({ $size }) => $size ?? 120}px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  margin: 0 auto;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

export const FadeIn = styled.div`
  animation: ${fadeIn} 0.35s ease;
`;

export const SkeletonStack = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 10}px;
`;

export const SkeletonRowSpread = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const TopBar = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 4px;

  h2 {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #0f172a;
    margin: 0;
  }

  .subtitle {
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
    font-weight: 500;
  }

  .updated {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

/* ───────── KPI row ───────── */

export const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 4px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

interface KpiToneProps {
  $tone?: 'green' | 'orange' | 'blue' | 'neutral';
}

const KPI_TONE: Record<
  'green' | 'orange' | 'blue' | 'neutral',
  { bg: string; color: string }
> = {
  green: { bg: '#e6f3ec', color: '#006633' },
  orange: { bg: '#fff1e0', color: '#f38725' },
  blue: { bg: '#e0ecff', color: '#1d4ed8' },
  neutral: { bg: '#f1f5f9', color: '#475569' },
};

export const KpiCell = styled.div<KpiToneProps & { $clickable?: boolean }>`
  padding: 16px 18px;
  border-right: 1px solid #eef2f7;
  display: flex;
  flex-direction: column;
  gap: 10px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 0.15s ease;
  border-radius: 10px;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 1200px) {
    &:nth-child(2n) {
      border-right: none;
    }
    &:nth-child(n + 3) {
      border-top: 1px solid #eef2f7;
    }
  }

  @media (max-width: 576px) {
    border-right: none;
    &:not(:first-child) {
      border-top: 1px solid #eef2f7;
    }
  }

  &:hover {
    ${({ $clickable }) =>
      $clickable &&
      css`
        background: #fbfcfe;
      `}
  }

  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    letter-spacing: 0.01em;
  }

  .icon {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${({ $tone }) => css`
      background: ${KPI_TONE[$tone || 'neutral'].bg};
      color: ${KPI_TONE[$tone || 'neutral'].color};
    `}
  }

  .value {
    font-size: 30px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .delta {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    background: #e6f3ec;
    color: #006633;

    &.warn {
      background: #fff1e0;
      color: #c76a13;
    }

    &.danger {
      background: #fee2e2;
      color: #b91c1c;
    }

    &.neutral {
      background: #f1f5f9;
      color: #475569;
    }
  }
`;

/* ───────── Generic card ───────── */

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  .titles h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
  }

  .titles .subtitle {
    display: block;
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
    font-weight: 500;
  }

  .actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const ChartBody = styled.div`
  flex: 1;
  min-height: 260px;
  display: flex;
  flex-direction: column;

  .recharts-responsive-container {
    flex: 1;
  }
`;

/* Legacy alias (kept so DashboardSkeleton keeps compiling) */
export const ChartCard = Card;

/* ───────── Toggle ───────── */

export const ToggleGroup = styled.div`
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 3px;
`;

export const ToggleButton = styled.button<{ $active?: boolean }>`
  border: none;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  color: ${({ $active }) => ($active ? '#0f172a' : '#64748b')};
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;

  box-shadow: ${({ $active }) =>
    $active ? '0 1px 3px rgba(15,23,42,0.08)' : 'none'};

  &:hover {
    color: #0f172a;
  }
`;

/* ───────── Main chart + map row ───────── */

export const MiddleRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const TripleRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/* ───────── Map card ───────── */

export const MapWrap = styled.div`
  position: relative;
  width: 100%;
  height: 340px;
  min-height: 340px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eef2f7;
  background: #eef4f9;
  flex-shrink: 0;

  .leaflet-container {
    width: 100% !important;
    height: 100% !important;
    min-height: 340px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: #eef4f9;
  }

  .leaflet-pane,
  .leaflet-top,
  .leaflet-bottom {
    z-index: 400;
  }

  .leaflet-tooltip {
    background: #0f172a;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 500;
  }

  .leaflet-tooltip-top:before {
    border-top-color: #0f172a;
  }

  .leaflet-control-attribution {
    font-size: 9px;
    background: rgba(255, 255, 255, 0.85);
  }
`;

export const MapLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 10px;
  padding: 0 4px;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;

  .item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
`;

/* ───────── Donut / legend ───────── */

export const DonutLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const DonutCenter = styled.div`
  position: relative;
  width: 100%;

  .center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  .center .big {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
  }
  .center .small {
    font-size: 10.5px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

export const LegendList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const LegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
  }
  .name {
    flex: 1;
    color: #0f172a;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .value {
    color: #64748b;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .percent {
    color: #94a3b8;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    min-width: 38px;
    text-align: right;
  }
`;

/* ───────── Ranked bars ───────── */

export const RankedBarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const RankedBarRow = styled.div`
  display: grid;
  grid-template-columns: 22px 1fr auto;
  gap: 12px;
  align-items: center;
  font-size: 13px;

  .rank {
    color: #94a3b8;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: 11px;
  }
  .row-body {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }
  .row-name {
    color: #0f172a;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12.5px;
  }
  .row-track {
    background: #f1f5f9;
    border-radius: 999px;
    height: 6px;
    overflow: hidden;
    width: 100%;
  }
  .row-fill {
    height: 100%;
    background: #f38725;
    border-radius: 999px;
    transition: width 0.6s ease;
  }
  .row-fill.green {
    background: #006633;
  }
  .row-value {
    color: #475569;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    min-width: 44px;
    text-align: right;
    font-size: 12px;
  }
`;

/* ───────── Composition (worker breakdown) ───────── */

export const CompositionSection = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;

  & + & {
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px dashed #e2e8f0;
    flex: 1;
  }

  .section-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 12px;
  }

  .legend-row {
    display: flex;
    flex-wrap: wrap;
    gap: 14px 22px;
    margin-top: 10px;
  }

  .legend-row .legend {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }

  .legend-row .dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .legend-row .name {
    color: #475569;
    font-weight: 500;
  }

  .legend-row .value {
    color: #0f172a;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .legend-row .percent {
    color: #94a3b8;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

export const StackedBar = styled.div`
  display: flex;
  width: 100%;
  height: 14px;
  border-radius: 999px;
  overflow: hidden;
  background: #f1f5f9;

  .segment {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: width 0.6s ease;
    position: relative;
  }

  .segment + .segment {
    border-left: 1px solid rgba(255, 255, 255, 0.6);
  }

  .segment-label {
    font-size: 10px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.02em;
  }
`;

export const StatTiles = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 10px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

type TileTone = 'green' | 'orange' | 'blue' | 'greenSoft';
const TILE_MAP: Record<
  TileTone,
  { bg: string; border: string; iconBg: string; iconColor: string }
> = {
  green: {
    bg: '#ffffff',
    border: '#eef2f7',
    iconBg: '#e6f3ec',
    iconColor: '#006633',
  },
  orange: {
    bg: '#ffffff',
    border: '#eef2f7',
    iconBg: '#fff1e0',
    iconColor: '#f38725',
  },
  blue: {
    bg: '#ffffff',
    border: '#eef2f7',
    iconBg: '#e0ecff',
    iconColor: '#1d4ed8',
  },
  greenSoft: {
    bg: '#f1faf4',
    border: '#c7e4d5',
    iconBg: '#ffffff',
    iconColor: '#006633',
  },
};

export const StatTile = styled.div<{ $tone: TileTone }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 130px;
  height: 100%;
  padding: 16px;
  border-radius: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  ${({ $tone }) => css`
    background: ${TILE_MAP[$tone].bg};
    border: 1px solid ${TILE_MAP[$tone].border};
  `}

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  }

  .icon {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${({ $tone }) => css`
      background: ${TILE_MAP[$tone].iconBg};
      color: ${TILE_MAP[$tone].iconColor};
    `}
  }

  .body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
  }

  .value {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .pct {
    font-size: 12px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0;
  }

  .label {
    font-size: 11px;
    color: #64748b;
    font-weight: 600;
    margin-top: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
`;

/* ───────── Gauge (sindicalização) ───────── */

export const GaugeCenter = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  .big {
    font-size: 30px;
    font-weight: 700;
    color: #006633;
    letter-spacing: -0.03em;
    margin-top: -60px;
  }

  .small {
    font-size: 11px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .breakdown {
    display: flex;
    gap: 18px;
    margin-top: 12px;
    width: 100%;
    justify-content: space-around;
  }

  .breakdown .item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
  }

  .breakdown strong {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    text-transform: none;
  }
`;

/* ───────── Side card ───────── */

export const SideCard = Card;

export const InsightPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e6f3ec;
  color: #006633;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: -0.01em;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  &.orange {
    background: #fff1e0;
    color: #c76a13;
  }

  &.danger {
    background: #fef2f2;
    color: #b91c1c;
  }

  &.blue {
    background: #e0ecff;
    color: #1d4ed8;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 32px 0;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
`;

/* ───────── Back-compat for DashboardSkeleton ───────── */

export const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

export const StatBox = styled.div`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 20px;
  min-height: 100px;
`;

export const KpiIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e6f3ec;
  color: #006633;
`;

export const ChartHeader = CardHeader;

export const ProductItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 4px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

export const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  .name {
    font-size: 13px;
    color: #0f172a;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .price {
    font-size: 11px;
    color: #64748b;
  }
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  color: #006633;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
  font-family: inherit;

  &:hover {
    color: #00451f;
  }
`;

export const AlertRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  margin-top: 8px;

  .icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #fff1e0;
    color: #f38725;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .icon.green {
    background: #e6f3ec;
    color: #006633;
  }
  .icon.danger {
    background: #fef2f2;
    color: #b91c1c;
  }
  .icon.orange {
    background: #fff1e0;
    color: #f38725;
  }
  .body {
    flex: 1;
    font-size: 12.5px;
    color: #334155;
    line-height: 1.4;

    strong {
      color: #0f172a;
      font-weight: 700;
    }
  }
`;
